import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
  type DehydratedState,
} from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setBaseUrl, setAuthTokenGetter } from "@workspace/api-client-react";
import NotFound from "@/pages/not-found";
import { Layout } from "@/components/layout";
import { SeoInitialProvider, type SeoInitialPayload } from "@/lib/seo-context";
import { LocaleProvider } from "@/lib/locale-context";
import { NON_DEFAULT_LOCALES, parseLocaleFromPath } from "@/lib/locale";
import {
  registerKnownDistrictSlugs,
  registerDistrictSlugsFromSitemap,
} from "@/lib/seo-cta";

// Pages — loaded lazily so Rollup emits each as its own chunk.
// The browser only fetches a chunk when the user navigates to that route,
// which keeps the initial JS payload to just the home page + shared vendors.
const Home = lazy(() => import("@/pages/home"));
const PropertiesList = lazy(() => import("@/pages/properties/index"));
const PropertyDetail = lazy(() => import("@/pages/properties/detail"));
const SeoPageRoute = lazy(() => import("@/pages/seo-page"));
const SeoIndexPage = lazy(() => import("@/pages/seo-index"));
const ContactPage = lazy(() => import("@/pages/contact"));
const MarketBenchmarks = lazy(() => import("@/pages/market-benchmarks"));
const MarketHistory = lazy(() => import("@/pages/market-history"));
const MarketCatalyst = lazy(() => import("@/pages/market-catalyst"));
const MarketDistrictRanking = lazy(() => import("@/pages/market-district-ranking"));
const ScarcityAlpha = lazy(() => import("@/pages/scarcity-alpha"));
const Methodology = lazy(() => import("@/pages/methodology"));
const TermsOfService = lazy(() => import("@/pages/terms"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const Fifa2030MarrakechRiadInvestment = lazy(() => import("@/pages/fifa-2030-marrakech-riad-investment"));

// Configure API base URL — point at our own api-server, which is a thin
// pass-through proxy for the upstream Riad Intelligence public API.
// Empty in dev so calls stay same-origin and Replit's path router
// forwards `/api/*` to the api-server artifact.
setBaseUrl((import.meta.env?.VITE_LEADS_API_BASE_URL ?? "").replace(/\/$/, ""));
setAuthTokenGetter(() => import.meta.env?.VITE_INTERNAL_API_KEY ?? null);

// Global react-query defaults: data is considered fresh for 30 seconds
// and is refetched when the tab regains focus. Upstream is live and
// serves enriched data, so we want visitors to see fresh values on
// navigation rather than holding onto a 5–10 minute snapshot.
//
// `refetchOnMount` is left at the default (`true`) — i.e. only refetch
// on mount when the entry is stale. This is critical for the SSR
// dehydrated state path: dehydrated entries arrive with `dataUpdatedAt`
// set by the prerender, so they're considered fresh for the first 30s
// of a visitor's session and are NOT immediately refetched on hydration.
// Without that, every prerendered route would fire a background refetch
// the moment it mounted, defeating most of the point of dehydration.
function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
      },
    },
  });
}

// Scrolls to the top of the page on every client-side navigation.
// Without this, navigating from a footer link (while scrolled down) keeps
// the scroll position at the bottom, making the new page appear blank.
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  return null;
}

// SEO routes use their own dedicated layout (lighter header, sticky CTAs,
// WhatsApp FAB, etc) so they wire up Layout themselves. Everything else
// keeps the standard RiadIntel Layout.

// All locale subtrees share the same internal route shape, but every
// route path is locale-prefixed (English uses no prefix). We deliberately
// avoid wouter's `nest` modifier here because it auto-prefixes nested
// <Link href="/x"> with the matched parent path, which would conflict with
// our explicit localizePath() helper and produce /<locale>/<locale>/x URLs.
function buildPath(prefix: string, sub: string): string {
  if (sub === "/") return prefix === "" ? "/" : prefix;
  return `${prefix}${sub}`;
}

// Minimal blank-screen fallback used while a route chunk is being fetched.
// Using min-h-screen ensures the layout doesn't collapse and cause a shift.
function PageFallback() {
  return <div className="min-h-screen bg-background" />;
}

function LocaleRoutes({ prefix }: { prefix: string }) {
  const p = (sub: string) => buildPath(prefix, sub);
  return (
    <Switch>
      <Route path={p("/r")} component={SeoIndexPage} />
      <Route path={p("/r/fifa-2030-marrakech-riad-investment")} component={Fifa2030MarrakechRiadInvestment} />
      <Route path={p("/r/:slug")} component={SeoPageRoute} />
      <Route path={p("/contact")} component={ContactPage} />
      <Route>
        <Layout>
          <Switch>
            <Route path={p("/")} component={Home} />
            <Route path={p("/properties")} component={PropertiesList} />
            <Route path={p("/properties/:id")} component={PropertyDetail} />
            <Route path={p("/market-benchmarks")} component={MarketBenchmarks} />
            <Route path={p("/market-history")} component={MarketHistory} />
            <Route path={p("/market-catalyst")} component={MarketCatalyst} />
            <Route path={p("/market-districts")} component={MarketDistrictRanking} />
            <Route path={p("/scarcity-alpha")} component={ScarcityAlpha} />
            <Route path={p("/methodology")} component={Methodology} />
            <Route path={p("/terms")} component={TermsOfService} />
            <Route path={p("/privacy")} component={PrivacyPolicy} />
            <Route component={NotFound} />
          </Switch>
        </Layout>
      </Route>
    </Switch>
  );
}

function Router() {
  return (
    <Switch>
      {/* Each non-default locale gets its own subtree, mirroring the English
          shape but with /<locale>/... paths. The inner Switch uses absolute
          paths, so <Link href="/<locale>/foo"> resolves correctly without
          wouter's `nest` auto-prefix interfering with our localizePath. */}
      {NON_DEFAULT_LOCALES.flatMap((loc) => [
        // wouter v3 path matching: `/:rest*` only matches a SINGLE segment
        // (not multi-segment paths like /fr/r/<slug>), and `/<loc>/*` does
        // not match the bare prefix `/<loc>`. So we register both forms:
        //   - `/<loc>` for the locale root (e.g. /fr)
        //   - `/<loc>/*` for everything below it (e.g. /fr/r/some-slug)
        // Both render LocaleRoutes; the inner Switch picks the right route.
        <Route key={`${loc}-root`} path={`/${loc}`}>
          <LocaleRoutes prefix={`/${loc}`} />
        </Route>,
        <Route key={`${loc}-rest`} path={`/${loc}/*`}>
          <LocaleRoutes prefix={`/${loc}`} />
        </Route>,
      ])}
      <Route>
        <LocaleRoutes prefix="" />
      </Route>
    </Switch>
  );
}

interface AppProps {
  seoInitial?: SeoInitialPayload | null;
  // SSR / prerender only: path to render. Ignored in the browser.
  ssrPath?: string;
  // SSR / prerender only: helmet collection context.
  helmetContext?: object;
}

function App({ seoInitial, ssrPath, helmetContext }: AppProps = {}) {
  // Seed the CTA builder's district registry from whatever the SSR payload
  // happens to carry (a full sitemap on /, or a per-page districtSlugs list
  // on /r/<slug>). Idempotent and synchronous so SSR + first paint are
  // already district-aware before hydration.
  registerKnownDistrictSlugs(seoInitial?.districtSlugs);
  registerDistrictSlugsFromSitemap(seoInitial?.sitemap);

  // Stable per-mount QueryClient. Created lazily so we don't allocate a fresh
  // one on every render — that would cause every component to refetch on
  // every parent re-render.
  const [queryClient] = useState(() => createAppQueryClient());

  // Derive the SSR locale from the URL so every render path (prerender,
  // hydration, browser nav) sees a consistent value. The provider's effect
  // hook keeps it in sync after hydration.
  const ssrLocale = ssrPath ? parseLocaleFromPath(ssrPath).locale : undefined;

  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        {/* HydrationBoundary picks up server-prefetched query data embedded
            in window.__SEO_INITIAL__.dehydratedState (set by the prerender)
            and feeds it into the QueryClient before any descendant useQuery
            mounts. This eliminates the loading-skeleton flash on SEO routes
            and keeps content visible across hydration. */}
        <HydrationBoundary
          state={(seoInitial?.dehydratedState as DehydratedState | undefined) ?? null}
        >
          <SeoInitialProvider value={seoInitial ?? null}>
            <LocaleProvider initialLocale={ssrLocale} ssrPath={ssrPath}>
              <TooltipProvider>
                <WouterRouter
                  base={import.meta.env.BASE_URL.replace(/\/$/, "")}
                  ssrPath={ssrPath}
                >
                  <ScrollToTop />
                  {/* Suspense catches lazy page chunks loading mid-navigation.
                      The fallback is a blank full-height div so the layout
                      doesn't collapse while the chunk arrives. */}
                  <Suspense fallback={<PageFallback />}>
                    <Router />
                  </Suspense>
                </WouterRouter>
                <Toaster />
              </TooltipProvider>
            </LocaleProvider>
          </SeoInitialProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
